import { deepClone } from './object.utilities';

export function assertArray<T = any>(subject: T | T[]): T[] {
  return subject == null ? [] : Array.isArray(subject) ? subject : [subject];
}

export function intersection(array1: any[], array2: any[]): any[] {
  return array1.filter((value) => array2.includes(value));
}

export function isEmptyArray(arr?: any[]): boolean {
  return !Array.isArray(arr) || !arr.length;
}

export function arrayFlatten<T>(arr: T[][]): T[] {
  return arr.reduce((a, b) => a.concat(b));
}

export function arraySum(arr: number[]): number {
  return arr.reduce((a, b) => a + b, 0);
}

export function isArrayShallowEqual<T>(arr1: T[], arr2: T[]): boolean {
  if (!arr1 || !arr2 || arr1.length !== arr2.length) {
    return false;
  }

  return arr1.every((item) => arr2.includes(item)) && arr2.every((item) => arr1.includes(item));
}

export function arrayToggleItem<T = any>(arr: T[], item: T, forceSelect?: boolean): T[] {
  if (!Array.isArray(arr)) {
    return arr;
  }

  const idx = arr.indexOf(item);

  if (idx > -1 && !forceSelect) {
    arr.splice(idx, 1);
  } else if (idx === -1) {
    arr.push(item);
  }

  return arr;
}

export function arrayUnique<T = any>(array: T[]): T[] {
  return Array.from(new Set(array));
}

export function arrayUniqueByProperty<T>(arr: T[], property: keyof T | any[]): T[] {
  const properties = assertArray(property);

  return arr.filter((item, i) => {
    const value = properties.reduce((val, prop) => val?.[prop], item);

    return i === arr.findIndex((other) => value === properties.reduce((val, prop) => val?.[prop], other));
  });
}

export function arrayFilterRepeatingHead<T>(arr: T[], property?: keyof T): T[] {
  let previousValue: unknown;
  let hasChanged = false;

  return arr.filter((item, idx) => {
    if (!hasChanged) {
      const value = property ? item[property] : item;

      hasChanged = idx > 0 && previousValue !== value;
      previousValue = value;
    }

    return hasChanged || !idx;
  });
}

export function arrayFilterRepeatingHeadTail<T>(arr: T[], property?: keyof T): T[] {
  return arrayFilterRepeatingHead(arrayFilterRepeatingHead(arr, property).reverse(), property).reverse();
}

export const TrackByKey =
  <T>(key: string) =>
  (idx: number, d: T) =>
    d?.[key] || idx;

export const Upsert = <T>(_array: T[], element: T, key: string, clone = true): T[] => {
  const array = clone ? deepClone(_array) : _array;
  const i = array.findIndex((_element) => _element[key] === element[key]);
  if (i > -1)
    array[i] = element; // (2)
  else array.push(element);
  return array;
};

export const ArrayContainsAll = <T>(src: T[] = [], values: T[] = []) => {
  if (src.length === 0 && values.length === 0) return true;
  if (src.length === 0) return false;
  return src.every((i) => values.includes(i));
};

export function ChangeIndexOfObject(arr, oldIndex, newIndex) {
  if (newIndex >= arr.length) {
    let k = newIndex - arr.length + 1;
    while (k--) {
      arr.push(undefined);
    }
  }
  arr.splice(newIndex, 0, arr.splice(oldIndex, 1)[0]);
}

export function getSumOfRecords<T extends Record<string, number>>(records: T[]): T {
  return records.reduce((sums: T, current) => {
    Object.entries(current).forEach(([key, value]) => ((sums as any)[key] = (sums[key] || 0) + value));

    return sums;
  }, {} as T);
}

export function removeDuplicates<T>(arr: T[]): T[] {
  return [...new Set(arr)];
}
